import React from 'react';
import { TMUA_PEARSON_TOKENS } from '../constants/tmuaPearsonTokens';
import { ESAT_MODULE_LABELS } from '../utils/classifyEsatModule';
import { TARA_MODULE_LABELS } from '../utils/classifyTaraPaperModule';
import { BMAT_MODULE_LABELS } from '../utils/classifyBmatPaperSection';
import { UCAT_SUBTEST_LABELS } from '../utils/ucatSubtests';
import { pearsonReviewRowStatus } from '../utils/pearsonReviewRowStatus';

function StatusLabel({ label, tone }) {
  const T = TMUA_PEARSON_TOKENS;
  const map = {
    complete: { bg: 'transparent', fg: '#000000' },
    incomplete: { bg: 'transparent', fg: T.statusIncomplete },
    unseen: { bg: '#FDECEA', fg: T.statusUnseen },
    flagged: { bg: 'transparent', fg: '#1565C0' },
    correct: { bg: T.pillCorrectBg, fg: T.pillCorrect },
    incorrect: { bg: T.pillIncorrectBg, fg: T.pillIncorrect },
  };
  const colors = map[tone] || map.complete;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 600,
        background: colors.bg,
        color: colors.fg,
      }}
    >
      {label}
    </span>
  );
}

function FlagIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="#42A5F5" stroke="#42A5F5" strokeWidth="1.5" aria-hidden="true">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" stroke="#42A5F5" />
    </svg>
  );
}

export default function TmuaPearsonReviewTable({
  mode,
  questions,
  userAnswers,
  flaggedQuestions,
  visitedQuestions,
  isQuestionAnswered,
  reviewTab,
  setReviewTab,
  onReviewRow,
  scoreLine,
  chrome = 'tmua',
  hideFlagged = false,
  paperColumnLabel,
  bmatSitting = false,
}) {
  const T = TMUA_PEARSON_TOKENS;
  const isPost = mode === 'post-exam';
  const isLnat = chrome === 'lnat';
  const isEsat = chrome === 'esat';
  const isTara = chrome === 'tara';
  const isTsa = chrome === 'tsa';
  const isUcat = chrome === 'ucat';

  const rows = (questions || []).map((q, idx) => {
    const answered = isQuestionAnswered(q);
    const visited = Boolean(visitedQuestions[q.id]);
    const flagged = Boolean(flaggedQuestions[q.id]);
    // An essay row is one the data says is an essay. It used to also count any
    // question in module 2, which is true for LNAT Section B but wrong for
    // TARA Problem Solving, UCAT Decision Making and TMUA Paper 2: those are
    // multiple choice, and being treated as essays meant they could never read
    // Correct or Incorrect after the exam, only Complete, Incomplete or Unseen.
    // Every real essay carries both flags, so the module number is not needed.
    const isEssayRow = q.is_essay === true
      || q.interaction_kind === 'essay_choice_group';
    let status;
    let tone;
    // Essay/writing rows (LNAT Section B and TARA Writing items) are never
    // marked Correct/Incorrect post-exam — Complete/Incomplete/Unseen only.
    if (isPost && isEssayRow) {
      const hasText = Boolean(String(userAnswers[q.id]?.text_response || '').trim());
      if (hasText) {
        status = 'Complete';
        tone = 'complete';
      } else if (visited) {
        status = 'Incomplete';
        tone = 'incomplete';
      } else {
        status = 'Unseen';
        tone = 'unseen';
      }
    } else if (isPost) {
      const result = pearsonReviewRowStatus(q, userAnswers[q.id], { visited, isQuestionAnswered });
      status = result.status;
      tone = result.tone;
    } else if (isLnat) {
      if (answered) {
        status = 'Answered';
        tone = 'complete';
      } else {
        status = 'Unanswered';
        tone = 'incomplete';
      }
    } else if (answered) {
      status = 'Complete';
      tone = 'complete';
    } else if (visited) {
      status = 'Incomplete';
      tone = 'incomplete';
    } else {
      status = 'Unseen';
      tone = 'unseen';
    }
    return { q, idx, answered, visited, flagged, status, tone, paper: q.tmuaPaper || 1, isEssayRow };
  });

  const counts = {
    all: rows.length,
    incomplete: rows.filter((r) => r.status === 'Incomplete').length,
    unanswered: rows.filter((r) => r.status === 'Unanswered' || !r.answered).length,
    unseen: rows.filter((r) => r.status === 'Unseen').length,
    flagged: rows.filter((r) => r.flagged).length,
    incorrect: rows.filter((r) => r.status === 'Incorrect').length,
  };

  const tabs = isLnat && !isPost
    ? [
        { id: 'all', label: `All (${counts.all})` },
        { id: 'unanswered', label: `Unanswered (${counts.unanswered})` },
      ]
    : isPost
    ? [
        { id: 'all', label: `All (${counts.all})` },
        { id: 'incorrect', label: `Incorrect (${counts.incorrect})`, hide: !counts.incorrect },
        { id: 'unseen', label: `Unseen (${counts.unseen})`, hide: !counts.unseen },
        { id: 'flagged', label: `Flagged (${counts.flagged})`, hide: hideFlagged },
      ].filter((t) => !t.hide)
    : [
        { id: 'all', label: `All (${counts.all})` },
        { id: 'incomplete', label: `Incomplete (${counts.incomplete})`, hide: !counts.incomplete },
        { id: 'unseen', label: `Unseen (${counts.unseen})`, hide: !counts.unseen },
        { id: 'flagged', label: `Flagged (${counts.flagged})`, hide: hideFlagged },
      ].filter((t) => !t.hide);

  const activeTab = tabs.some((t) => t.id === reviewTab) ? reviewTab : 'all';
  const filtered = rows.filter((r) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'incomplete') return r.status === 'Incomplete';
    if (activeTab === 'unanswered') return r.status === 'Unanswered' || !r.answered;
    if (activeTab === 'unseen') return r.status === 'Unseen';
    if (activeTab === 'flagged') return r.flagged;
    if (activeTab === 'incorrect') return r.status === 'Incorrect';
    return true;
  });

  const primaryLabel = activeTab === 'all'
    ? 'Review All'
    : activeTab === 'incomplete'
      ? 'Review Incomplete'
      : activeTab === 'unanswered'
        ? 'Review Unanswered'
      : activeTab === 'unseen'
        ? 'Review Unseen'
        : activeTab === 'flagged'
          ? 'Review Flagged'
          : 'Review Incorrect';

  const th = {
    padding: '10px 16px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: '#607D8B',
    borderBottom: '1px solid #ECEFF1',
    textAlign: 'left',
  };

  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
        background: '#F2F3F5',
        color: '#14161F',
        fontFamily: 'Tahoma, "Segoe UI", sans-serif',
        padding: '20px 36px 80px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Review</h1>
        <button
          type="button"
          onClick={() => {
            const first = filtered[0];
            if (first) onReviewRow(first.idx, first.q);
          }}
          style={{
            background: T.reviewTeal,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: 3,
            padding: '7px 16px',
            fontFamily: 'Tahoma, "Segoe UI", sans-serif',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {primaryLabel}
        </button>
      </div>
      {scoreLine ? (
        <div style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>{scoreLine}</div>
      ) : null}

      <div style={{ display: 'flex', borderBottom: '2px solid #CFD8DC', marginBottom: 16 }}>
        {tabs.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setReviewTab(tab.id)}
              style={{
                flex: 1,
                background: 'none',
                border: 'none',
                borderBottom: active ? `3px solid ${T.reviewTeal}` : '3px solid transparent',
                marginBottom: -2,
                padding: '10px 8px',
                fontFamily: 'Tahoma, "Segoe UI", sans-serif',
                fontSize: 13,
                cursor: 'pointer',
                color: active ? T.reviewTeal : '#546E7A',
                fontWeight: active ? 700 : 500,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div style={{ background: '#FFFFFF', borderRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>QUESTION</th>
              <th style={th}>TITLE</th>
              {isPost ? <th style={th}>{isLnat || isTsa ? 'SECTION' : isUcat ? 'SUBTEST' : (isEsat || isTara) ? 'MODULE' : 'PAPER'}</th> : null}
              <th style={th}>STATUS</th>
              {hideFlagged ? null : <th style={th}>FLAGGED</th>}
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.q.id ?? row.idx} style={{ borderBottom: '1px solid #ECEFF1' }}>
                <td style={{ padding: '10px 16px' }}>{row.idx + 1}</td>
                <td style={{ padding: '10px 16px' }}>Question</td>
                {isPost ? (
                  <td style={{ padding: '10px 16px' }}>
                    {paperColumnLabel
                      || (isLnat
                        ? (row.isEssayRow ? 'Section B' : 'Section A')
                        : isTsa
                          ? (Number(row.q.tmuaPaper) === 2 ? 'Section 2' : 'Section 1')
                        : isEsat
                          ? (ESAT_MODULE_LABELS[row.q.pearsonModule] || row.q.pearsonModule || '')
                          : isTara
                            ? ((bmatSitting || BMAT_MODULE_LABELS[row.q.pearsonModule])
                              ? (BMAT_MODULE_LABELS[row.q.pearsonModule] || TARA_MODULE_LABELS[row.q.pearsonModule] || row.q.pearsonModule || '')
                              : (TARA_MODULE_LABELS[row.q.pearsonModule] || row.q.pearsonModule || ''))
                            : isUcat
                              ? (UCAT_SUBTEST_LABELS[row.q.pearsonModule] || row.q.pearsonModule || '')
                              : (row.q.tmuaPaperLabel || `Paper ${row.paper}`))}
                  </td>
                ) : null}
                <td style={{ padding: '10px 16px' }}><StatusLabel label={row.status} tone={row.tone} /></td>
                {hideFlagged ? null : (
                  <td style={{ padding: '10px 16px' }}>
                    {row.flagged ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#1565C0' }}>
                        Yes <FlagIcon />
                      </span>
                    ) : 'No'}
                  </td>
                )}
                <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => onReviewRow(row.idx, row.q)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: T.reviewTeal,
                      fontFamily: 'Tahoma, "Segoe UI", sans-serif',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
